import type {
  ModuleUsagePrices,
  PlanVisibilityScope,
  Software,
  SoftwareModule,
  SoftwarePreview,
} from '../software.types';
import { ProductRelationDetailModel } from './ProductRelationDetailModel';
import { SoftwareCategoryModel } from '@repositories/softwareCategoryRepository/models/SoftwareCategoryModel';
import { SoftwareSimpleModel } from './SoftwareSimpleModel';

export class SoftwareModel {
  private readonly _dto: Software;
  private readonly _categories: SoftwareCategoryModel[];
  private readonly _relatedProducts: ProductRelationDetailModel[];

  constructor(dto?: Software) {
    this._dto = dto || {} as Software;
    this._categories = dto?.categories ? dto.categories.map(cat => new SoftwareCategoryModel(cat)) : [];
    this._relatedProducts = dto?.relatedProducts
      ? dto.relatedProducts.map(el => new ProductRelationDetailModel(el))
      : [];
  }

  get originDTO(): Software {
    return this._dto;
  }

  public get providerId(): string | null {
    return this._dto.providerId || null;
  }

  public get id(): string {
    return this._dto.id || '';
  }

  public get productCode(): string {
    return this._dto.productCode || '';
  }

  public get name(): string {
    return this._dto.name || '';
  }

  public get description(): string | undefined {
    return this._dto.description || '';
  }

  public get url(): string | undefined {
    return this._dto.url || '';
  }

  public get companyName(): string | undefined {
    return this._dto.companyName || '';
  }

  public get iconUrl(): string | undefined {
    return this._dto.iconUrl || '';
  }

  public get thumbnailUrl(): string | undefined {
    return this._dto.thumbnailUrl;
  }

  /**
   * @deprecated 모듈 목록은 ProductPlanModel의 usageMetricList를 사용해야 함
   */
  public get modules(): SoftwareModule[] {
    return this._dto.modules || [];
  }

  public get categories(): SoftwareCategoryModel[] {
    return this._categories;
  }

  public get categoryNames(): string {
    return this.categories?.map(({ name }) => name).join(', ') ?? '';
  }

  public get moduleUsagePrices(): ModuleUsagePrices {
    return this._dto.moduleUsagePrices || {};
  }

  public get currencyUnit(): CurrencyUnit {
    return this._dto.currencyUnit || 'KRW';
  }

  public get overview(): string {
    return this._dto.overview || '';
  }

  public get features(): string {
    return this._dto.features || '';
  }

  public get advantages(): string {
    return this._dto.advantages || '';
  }

  public get previews(): SoftwarePreview[] {
    return this._dto.softwarePreviews || [];
  }

  public get hashtags(): string[] {
    return this._dto.hashtags || [];
  }

  public get isSubscribing(): boolean {
    return this._dto.isSubscribing ?? false;
  }

  public get planVisibilityScope(): PlanVisibilityScope {
    return this._dto.planVisibilityScope || 'ALL_USERS';
  }

  public get relatedProducts(): ProductRelationDetailModel[] {
    return this._relatedProducts;
  }

  public get simpleModel(): SoftwareSimpleModel {
    return new SoftwareSimpleModel({
      id: this._dto.id || '',
      productCode: this._dto.productCode || '',
      name: this._dto.name || '',
      description: this._dto.description,
      url: this._dto.url,
      companyName: this._dto.companyName,
      iconUrl: this._dto.iconUrl,
      thumbnailUrl: this._dto.thumbnailUrl,
      category: this._categories[0],
      categoryCount: this._categories.length,
      isSubscribing: this._dto.isSubscribing,
      planVisibilityScope: this._dto.planVisibilityScope || 'ALL_USERS',
    });
  }
}
